import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const Process: React.FC = () => {
  const stepsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('process-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    stepsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const { t } = useTranslation();

  const processSteps = [
    {
      step: '01',
      title: t('process.step01title'),
      description: t('process.step01desc')
    },
    {
      step: '02',
      title: t('process.step02title'),
      description: t('process.step02desc')
    },
    {
      step: '03',
      title: t('process.step03title'),
      description: t('process.step03desc')
    },
    {
      step: '04',
      title: t('process.step04title'),
      description: t('process.step04desc')
    },
    {
      step: '05',
      title: t('process.step05title'),
      description: t('process.step05desc')
    },
    {
      step: '06',
      title: t('process.step06title'),
      description: t('process.step06desc')
    }
  ];

  return (
    <section id="process" className="section-padding steel-bg">
      <div className="container-custom">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('process.title')}
          </h2>
          <p className="text-base sm:text-xl text-gray-300 max-w-3xl mx-auto">
            {t('process.description')}
          </p>
        </div>

        {/* Process timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-accent-green via-accent-orange to-accent-green"></div>
          
          <div className="space-y-12">
            {processSteps.map((step, index) => (
              <div 
                key={index}
                id={`process-step-${index}`}
                ref={(el) => { stepsRef.current[index] = el; }}
                className={`process-step relative flex flex-col lg:flex-row items-center ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Step number circle */}
                <div className="process-circle flex-shrink-0 w-20 h-20 bg-tech-dark border-2 border-accent-green rounded-full flex items-center justify-center text-accent-green font-bold text-lg relative z-10">
                  {step.step}
                </div>

                {/* Content card */}
                <div className={`flex-1 mt-6 lg:mt-0 lg:mx-8 ${
                  index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'
                }`}>
                  <div className="process-card tech-card max-w-lg mx-auto md:mx-0">
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {step.description}
                    </p>
                    
                    {/* Additional details for specific steps */}
                    {index === 0 && (
                      <div className="mt-4 pt-4 border-t border-[#2a2a2a]">
                        <div className="flex flex-wrap gap-2">
                          <span className="px-3 py-1 bg-tech-dark rounded-full text-xs text-gray-400">{t('process.calculations')}</span>
                          <span className="px-3 py-1 bg-tech-dark rounded-full text-xs text-gray-400">{t('process.simulations')}</span>
                          <span className="px-3 py-1 bg-tech-dark rounded-full text-xs text-gray-400">{t('process.optimization')}</span>
                        </div>
                      </div>
                    )}
                    
                    {index === 4 && (
                      <div className="mt-4 pt-4 border-t border-[#2a2a2a]">
                        <div className="flex items-center space-x-2 text-accent-green">
                          <span className="text-2xl">📞</span>
                          <span className="text-sm font-medium">{t('process.support247')}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden lg:block flex-1"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom section - Email CTA + Guarantee badge side by side */}
        <div className="mt-16">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            {/* Email CTA - Left */}
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-sm mb-4">
                Dieselgenerátory, batériové úložiská, kogeneračné jednotky, trafostanice a VN rozvádzače — od návrhu po servis.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  placeholder="Váš email"
                  className="flex-1 px-5 py-3 bg-[#161616] border border-[#2a2a2a] rounded-full text-white placeholder-gray-500 focus:border-accent-green focus:outline-none text-sm"
                />
                <button className="px-6 py-3 bg-accent-green hover:bg-[#d4ff33] text-black font-semibold rounded-full transition-colors duration-300 text-sm whitespace-nowrap">
                  Kontaktovať
                </button>
              </div>
            </div>
            
            {/* Guarantee badge - Right */}
            <div className="flex justify-center md:justify-end">
              <div className="inline-flex items-center space-x-3 bg-accent-green/10 border border-accent-green/30 rounded-lg px-6 py-4">
                <div className="w-10 h-10 bg-accent-green rounded-full flex items-center justify-center">
                  <span className="text-tech-dark font-bold">✓</span>
                </div>
                <div className="text-left">
                  <div className="font-semibold text-white">{t('process.guarantee')}</div>
                  <div className="text-sm text-gray-400">{t('process.guaranteeDesc')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
